/** PTL Operations → ODA Management (PTL_ODA_REWORK_DEV_LOG.md). */
export const ODA_EYEBROW = 'PTL Operations'

export const ODA_ROUTES = {
  pincodes: '/ptl/oda',
  bands: '/ptl/oda/bands',
  upload: '/ptl/oda/upload',
  history: '/ptl/oda/history',
  shipments: '/ptl/oda/shipments',
}

/** Sections of the ODA Management page (PtlPage tabs). */
export const odaTabs = () => [
  { key: ODA_ROUTES.pincodes, label: 'ODA Pincodes', to: ODA_ROUTES.pincodes },
  { key: ODA_ROUTES.bands, label: 'ODA Bands', to: ODA_ROUTES.bands },
  { key: ODA_ROUTES.shipments, label: 'ODA Shipments', to: ODA_ROUTES.shipments },
]

/** Where a pincode's extra days come from, as ops read it, with the chip tone (the order the EDD job checks them). */
export const SOURCE = {
  HAND: { label: 'Set by hand', tone: 'pending' },
  TYPE: { label: 'ODA type', tone: 'done' },
  BAND: { label: 'Km band', tone: 'done' },
  FALLBACK: { label: 'Fallback', tone: 'none' },
}

/** ODA Pincodes filters; the KPI cards switch them (?filter=). */
export const ODA_FILTERS = ['all', 'type', 'band', 'both', 'hand']

/** Same bound as the backend: catches typos. */
export const MAX_EXTRA_DAYS = 30

/** ODA types of a courier zone map row (the courier's ODA charge column); blank = no type (Regular, or ODA by its band). */
export const ODA_TYPES = ['ODA 1', 'ODA 2', 'ODA 3']

/** ODA Shipments filters; the KPI cards switch them (?filter=). */
export const SHIPMENT_FILTERS = ['all', 'type', 'band', 'hand', 'fallback', 'pastEdd']

/** ODA history actions as ops read them. */
export const HISTORY_ACTION = {
  mark: { label: 'ODA type set', tone: 'pending' },
  unmark: { label: 'Made Regular', tone: 'none' },
  band: { label: 'Km band', tone: 'done' },
  days: { label: 'Days by hand', tone: 'done' },
  upload: { label: 'ODA zone upload', tone: 'none' },
}

/** Keys of PTL_ODA_BANDS.categoryDays: the days of each ODA type. */
export const ODA_TYPE_KEYS = ['ODA1', 'ODA2', 'ODA3']

/** "ODA1" → "ODA 1", as the zone maps write it. */
export const typeLabel = (key) => key.replace('ODA', 'ODA ')

/** "ODA 1" → "ODA1", the key of PTL_ODA_BANDS.categoryDays. */
export const typeKey = (odaType) => (odaType ? odaType.replace(/\s+/g, '').toUpperCase() : '')

const plusDays = (n) => `+${n} day${n === 1 ? '' : 's'}`

/**
 * The EDD rule of an ODA type / band / days by hand, in the words the backend writes (PtlOdaDays.rule): days by hand
 * win, then the ODA type's days (a type whose days are not set up takes the fallback, never the band), then the band's
 * days, then the fallback.
 */
export const odaRule = ({ odaType, band, handDays }, bands) => {
  const b = bands?.bands?.find((x) => x.key === band)
  const fallback = plusDays(bands?.fallbackDays ?? 3)
  if (handDays != null) return `Days set by hand → ${plusDays(handDays)}`
  if (odaType) {
    const typeDays = bands?.categoryDays?.[typeKey(odaType)]
    return typeDays != null ? `${odaType} → ${plusDays(typeDays)}${b ? ` (${b.label} band not applied)` : ''}` : `${odaType} days not set up → default ${fallback}`
  }
  if (b && b.days != null) return `No ODA type · ${b.label} band → ${plusDays(b.days)}`
  return `No ODA type or band days → default ${fallback}`
}

/** The days the type or band gives (what "Extra EDD days" fills in), or null with neither. */
export const autoDays = ({ odaType, band }, bands) => {
  if (odaType) return bands?.categoryDays?.[typeKey(odaType)] ?? bands?.fallbackDays ?? 3
  const b = bands?.bands?.find((x) => x.key === band)
  return b && b.days != null ? b.days : null
}

/**
 * PTL Serviceability shows the ODA category of its list rows: since the ODA rework the EDD no longer reads it (ODA comes
 * from the courier zone map, ODA Management), so the screen says so instead of a days rule.
 */
export const categoryRule = (category) => (category ? 'Not used for the EDD: ODA days come from the courier zone map (ODA Management)' : '')

/**
 * When an ODA change reaches the EDD, said the same on every ODA screen: the EDD job runs at pickup and again after
 * each tracking update of an open shipment, and an EDD it sets only ever moves later.
 */
export const EDD_TIMING = 'at pickup, and for open shipments at their next tracking update if the date moves later (an EDD never moves earlier)'
